import { ACCESS_BLOCKED_MESSAGE } from './printing-errors';
import { formatMoment, problemText } from './printing-format';
import { PRINTING_AGENTS_PATH, PRINTING_SETTINGS_PATH } from './printing-paths';
import type { PrintingProblem, PrintingStatus } from '../types/printing.types';

/** Texto da faixa no desligamento geral, igual com a loja pausada ou não. */
export const PLATFORM_DISABLED_BANNER =
  'Impressão desativada temporariamente pela equipe do Vapt Menu: os pedidos não estão saindo no papel e precisam ser aceitos à mão';

const SETTINGS_PATH = PRINTING_SETTINGS_PATH;

/**
 * Change print-agent: o computador recusado por protocolo continua ligado e conectado,
 * mas não imprime. "Offline" mandaria a equipe religar um computador que está ligado.
 */
export const UPDATE_REQUIRED_BANNER =
  'Computador da impressão precisa ser atualizado: os pedidos não estão saindo no papel';

/** Para quem não pode pedir a atualização: quem pode. */
export const UPDATE_REQUIRED_ASK_OWNER = 'Avise o dono ou o gerente para atualizar o computador.';

export type PrintingNotice =
  | {
      kind: 'banner';
      lines: string[];
      /** O texto curto do resumo dos avisos do topo (decisão D3): um problema e "e mais N problemas". */
      summary: string;
      /**
       * `urgent` quando o papel para de sair agora (computador offline ou
       * desatualizado, impressora inacessível ou não encontrada, impressão
       * suspensa ou desligada); `info` quando só falta configurar (comanda sem
       * impressora, impressora sem computador ou de um computador revogado),
       * um estado que dura dias e não pode tirar do resumo quem está esperando.
       */
      level: 'urgent' | 'info';
      link: { to: string; label: string } | null;
    }
  | { kind: 'paused'; text: string; link: { to: string; label: string } | null }
  | null;

/** Os problemas que param o papel agora; os demais são de configuração. */
const STOPS_PAPER: ReadonlySet<PrintingProblem['code']> = new Set(['agent_offline', 'printer_unreachable', 'printer_missing']);

/** O problema para o papel agora (e não só falta configurar). */
export function stopsPaper(problem: PrintingProblem): boolean {
  return STOPS_PAPER.has(problem.code);
}

/**
 * O resumo da faixa: o primeiro problema e, quando há mais, "e mais N
 * problemas". A lista inteira empurrava os outros urgentes para fora da
 * linha do topo; a faixa da lista aberta diz todos.
 */
export function joinSummaries(first: string, rest: number): string {
  if (rest <= 0) return first;
  return `${first} e mais ${rest === 1 ? '1 problema' : `${rest} problemas`}`;
}

/** O problema em poucas palavras, para o resumo dos avisos do topo. */
export function problemSummary(problem: PrintingProblem): string {
  const name = problem.name ?? '';
  switch (problem.code) {
    case 'agent_offline':
      return 'Computador da impressão offline';
    case 'printer_unreachable':
      return `Impressora ${name} inacessível`;
    case 'printer_missing':
      return `Impressora ${name} não encontrada`;
    case 'printer_without_agent':
      return `Impressora ${name} sem computador`;
    case 'agent_revoked':
      return `Impressora ${name} de um computador revogado`;
    case 'station_without_printer':
      return `Comanda ${name} sem impressora`;
  }
}

/**
 * Decide o que o `PanelLayout` mostra sobre a impressão. A ordem importa:
 * - inativa (nunca ativada ou desativada pela loja): nada;
 * - assinatura pendente e desligamento geral vencem a pausa, porque retomar
 *   não faz o papel voltar a sair nesses casos;
 * - pausada: o aviso discreto, e os problemas de computador, impressora e
 *   cupom ficam de fora (a API continua a calculá-los);
 * - ativa e sem pausa: a faixa dos problemas.
 */
export function printingNotice(
  status: PrintingStatus,
  can: { settingsWrite: boolean; billingRead: boolean },
): PrintingNotice {
  if (!status.active) return null;

  const settingsLink = can.settingsWrite ? { to: SETTINGS_PATH, label: 'Abrir a aba Impressão' } : null;

  if (status.accessBlocked) {
    return {
      kind: 'banner',
      lines: [ACCESS_BLOCKED_MESSAGE],
      summary: 'Impressão suspensa pela assinatura',
      level: 'urgent',
      link: can.billingRead ? { to: '/billing', label: 'Ver assinatura' } : null,
    };
  }

  if (status.platformDisabled) {
    return {
      kind: 'banner',
      lines: [PLATFORM_DISABLED_BANNER],
      summary: 'Impressão desativada pela equipe do Vapt Menu',
      level: 'urgent',
      link: settingsLink,
    };
  }

  if (status.paused) {
    const since = status.pausedAt ? ` desde ${formatMoment(status.pausedAt)}` : '';
    return {
      kind: 'paused',
      text: `Impressão pausada${since}`,
      link: can.settingsWrite ? { to: SETTINGS_PATH, label: 'Retomar na aba Impressão' } : null,
    };
  }

  // Recusado por protocolo: o problema continua `agent_offline`, só o texto muda. Quem
  // pode pedir vai direto à lista de computadores, onde está "Atualizar"; os demais são
  // orientados a avisar quem pode.
  if (status.agent?.updateRequired === true) {
    const updateLine = can.settingsWrite
      ? UPDATE_REQUIRED_BANNER
      : `${UPDATE_REQUIRED_BANNER}. ${UPDATE_REQUIRED_ASK_OWNER}`;
    const others = status.problems.filter((problem) => problem.code !== 'agent_offline');
    return {
      kind: 'banner',
      lines: [updateLine, ...others.map(problemText)],
      summary: joinSummaries('Computador da impressão precisa ser atualizado', others.length),
      level: 'urgent',
      link: can.settingsWrite ? { to: PRINTING_AGENTS_PATH, label: 'Atualizar o computador' } : null,
    };
  }

  if (status.problems.length === 0) return null;
  // No resumo, o problema que para o papel vem primeiro.
  const ranked = [...status.problems.filter(stopsPaper), ...status.problems.filter((problem) => !stopsPaper(problem))];
  return {
    kind: 'banner',
    lines: status.problems.map(problemText),
    summary: joinSummaries(problemSummary(ranked[0]), ranked.length - 1),
    level: ranked.some(stopsPaper) ? 'urgent' : 'info',
    link: settingsLink,
  };
}

